import { Middleware } from 'redux';
import { AppActions } from './types';
import { LOGOUT_SUCCESS, MOBILE_SESSION_OUT } from './types/login';
import en from '../lang/en-us';
import { REGISTER_DEVICE } from './types/mobile';

export const CALL_API = 'CALL_API';
export const BASE_URL = process.env.REACT_APP_BASE_URL;

export const getPlatformWisePayload = (action: null | string = null) => {
    const _window: any = window;
    if (!!_window.cordova) {
        const { platform, version, uuid, model } = _window.device || {};
        return {
            METADATA: {
                appType: 'MOBILE',
                OSVersion: version,
                deviceName: model,
                deviceOS: platform,
            },
            LOGOUT_ACTION: !!action ? action : MOBILE_SESSION_OUT,
        };
    }
    return {
        METADATA: {
            appType: 'WEB',
            OSVersion: '',
            deviceName: '',
            deviceOS: window.navigator.userAgent,
        },
        LOGOUT_ACTION: LOGOUT_SUCCESS,
    };
};

const DEFAULT_HEADERS = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    Expires: 0,
};

class AuthError extends Error {}

class _Error extends Error {
    constructor(message: string, code: string) {
        super();
        this.name = code;
        this.message = message;
    }
}

interface CustomError {
    error: boolean;
    message: string;
    code: number;
}

export interface ResponseBody<T> {
    type: AppActions;
    body: T;
    error?: any;
}

interface ApiResBody {
    data: any;
    count: number;
    error: string[];
    success: boolean;
}

export interface ResponseType {
    data?: any;
    error?: null | CustomError;
    status?: number;
}

export interface ActionReqType {
    types: string[];
    body?: any;
    enableFixture?: boolean;
    json?: string;
    url: string;
    method: 'POST' | 'GET' | 'PUT' | 'DELETE';
    contentType?: any;
    ignoreUnAuthAccess: boolean;
    enableForProduction: boolean;
}

async function invokeAPI({
    endpoint,
    token,
    config,
    headerContent,
    enableFixture,
    json,
    contentType,
    ignoreUnAuthAccess,
    enableFixtureForProduction,
}: {
    endpoint: string;
    token: string;
    config: any;
    headerContent?: object;
    enableFixture?: boolean;
    json?: string;
    contentType?: any;
    ignoreUnAuthAccess?: boolean;
    enableFixtureForProduction: boolean;
}): Promise<ResponseType> {
    let headers = token
        ? { ...DEFAULT_HEADERS, Authorization: `Bearer ${token}` }
        : { ...DEFAULT_HEADERS };
    const updatedConfig = {
        ...config,
        headers: { ...headers, ...headerContent },
        // redirect :"manual"
    };
    const fixtureEnabled =
        enableFixture && (enableFixtureForProduction || process.env.NODE_ENV === 'development');
    const url = fixtureEnabled ? `/fixtures/${json}` : `${BASE_URL}${endpoint}`;
    const response = fixtureEnabled ? await fetch(url) : await fetch(url, updatedConfig);

    if (response.status === 401) {
        if (!ignoreUnAuthAccess) {
            throw new AuthError(en.networkText.timeOut);
        }
        return {};
    }
    // console.log(response)

    let body: any;
    try {
        if (typeof contentType === 'undefined') {
            body = await response.json();
        } else if (contentType === 'application/pdf' || contentType === 'application/vnd.ms-excel') {
            body = await response.blob();
        }
    } catch (err: any) {}

    if (response.status >= 400) {
        const { errors = [], ...rest } = body || {};
        const [firstError] = errors;
        throw new _Error(firstError?.message || en.networkText.unableToProcess, firstError?.code);
    }
    return { data: body, error: null, status: response.status };
}

export const middleWareDispatch: Middleware = (store) => (next) => async (action) => {
    if (typeof action[CALL_API] === 'undefined') {
        return next(action);
    }
    const { LOGOUT_ACTION, METADATA } = getPlatformWisePayload();
    const {
        url,
        types,
        body = {},
        method,
        json,
        enableFixture = false,
        contentType,
        ignoreUnAuthAccess = false,
        enableForProduction = false,
    }: ActionReqType = action[CALL_API];
    const [successType, requestType, errorType] = types;
    try {
        const {
            authReducer: { token = '', mobile, email, pan, pekrn },
        } = store.getState();
        const payload = {
            endpoint: url,
            token,
            config: {
                method,
                ...(method === 'POST'
                    ? {
                          body: JSON.stringify({
                              email,
                              mobile,
                              pan,
                              pekrn,
                              ...body,
                              metaData: METADATA,
                          }),
                      }
                    : {}),
            },
            enableFixture,
            json,
            contentType,
            enableFixtureForProduction: enableForProduction,
        };
        if (requestType) next({ type: requestType });
        const { data, error, status }: ResponseType = await invokeAPI({
            ...payload,
            ignoreUnAuthAccess,
        });
        if (data || !error) {
            if (successType === LOGOUT_ACTION) {
                setTimeout(() => {
                    window.location.reload();
                }, 0);
            }
            next({ type: successType, body: data });
        }
        return { body: data, error, status };
    } catch (error) {
        if (error instanceof AuthError) {
            next({ type: LOGOUT_ACTION, isTimeout: true });
        }
        if (errorType) next({ type: errorType });
        throw error;
    }
};
